import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Lock, EyeOff, Server, Ban, FileText, CheckCircle, ArrowLeft, Globe, Database, Trash2, UserCheck, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import ContactFormModal from '../components/contact/ContactFormModal';

export default function PrivacyPage() {
  const navigate = useNavigate();
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const lastUpdated = "January 28, 2026";

  return (
    <div className="min-h-screen bg-[#0a0a18] text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#0a0a18]/90 backdrop-blur-lg border-b border-[#2d2d44]/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/mpm-logo.png" alt="MyPatriotMail" className="w-10 h-10 object-contain" />
            <span className="text-lg font-bold">MyPatriotMail</span>
          </div>
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        {/* Title Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#6D4AFF]/20 rounded-2xl mb-6">
            <Shield className="w-8 h-8 text-[#6D4AFF]" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-black mb-4" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            Privacy Policy
          </h1>
          <p className="text-gray-400">
            Last Updated: {lastUpdated}
          </p>
        </div>

        {/* Our Promise Banner */}
        <div className="bg-gradient-to-r from-[#6D4AFF]/20 to-green-500/10 border-2 border-[#6D4AFF] rounded-xl p-6 mb-10">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-[#6D4AFF]/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Lock className="w-6 h-6 text-[#6D4AFF]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#6D4AFF] mb-2">Our Iron-Clad Promise</h2>
              <p className="text-gray-300 text-lg">
                <strong className="text-white">We will NEVER sell, rent, trade, or share your personal information with anyone.</strong> Period. 
                Your data is yours. Your privacy is sacred. This is not just a policy—it's our founding principle.
              </p>
            </div>
          </div>
        </div>

        {/* Table of Contents */}
        <div className="bg-[#1a1a2e] border border-[#2d2d44] rounded-xl p-6 mb-10">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#6D4AFF]" />
            Table of Contents
          </h3>
          <nav className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            {[
              { num: '1', title: 'Our Privacy Commitment' },
              { num: '2', title: 'Information We Collect' },
              { num: '3', title: 'What We Do NOT Collect' },
              { num: '4', title: 'How We Use Your Information' },
              { num: '5', title: 'Data Protection & Encryption' },
              { num: '6', title: 'No Data Sales - Ever' },
              { num: '7', title: 'Third-Party Services' },
              { num: '8', title: 'Data Retention' },
              { num: '9', title: 'Your Rights' },
              { num: '10', title: 'Contact Us' }
            ].map((item) => (
              <a 
                key={item.num}
                href={`#section-${item.num}`}
                className="text-gray-400 hover:text-[#6D4AFF] transition-colors py-1"
              >
                {item.num}. {item.title}
              </a>
            ))}
          </nav>
        </div>

        {/* Privacy Sections */}
        <div className="space-y-10">
          {/* Section 1 - Our Privacy Commitment */}
          <section id="section-1" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#6D4AFF] rounded-lg flex items-center justify-center text-sm">1</span>
              Our Privacy Commitment
            </h2>
            <div className="text-gray-300 space-y-4 pl-11">
              <p>
                At MyPatriotMail, privacy isn't just a feature—it's the foundation of everything we do. We built this 
                platform because we believe Americans deserve a communication tool that respects their fundamental right 
                to privacy.
              </p>
              <div className="bg-[#1a1a2e] border border-[#2d2d44] rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">Our Core Privacy Principles:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-bold text-white">Zero Data Sales</span>
                      <p className="text-sm text-gray-400">We never sell your information to anyone</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-bold text-white">No Advertising</span>
                      <p className="text-sm text-gray-400">No ads, no tracking, no profiling</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-bold text-white">End-to-End Encryption</span>
                      <p className="text-sm text-gray-400">AES-256 encryption on all emails</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-bold text-white">Minimal Data Collection</span>
                      <p className="text-sm text-gray-400">Only what's essential to operate</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 2 - Information We Collect */}
          <section id="section-2" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#6D4AFF] rounded-lg flex items-center justify-center text-sm">2</span>
              Information We Collect
            </h2>
            <div className="text-gray-300 space-y-4 pl-11">
              <p>
                We collect only the minimum information necessary to provide you with a functional, secure email service:
              </p>
              
              <div className="space-y-4">
                <div className="bg-[#1a1a2e] border border-[#2d2d44] rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <UserCheck className="w-5 h-5 text-[#6D4AFF]" />
                    <span className="font-bold text-white">Account Information</span>
                  </div>
                  <ul className="text-sm space-y-2 text-gray-300 pl-8">
                    <li>• Your chosen username (email prefix)</li>
                    <li>• Display name (optional)</li>
                    <li>• Encrypted password (we cannot see your password)</li>
                    <li>• Account creation date</li>
                  </ul>
                </div>

                <div className="bg-[#1a1a2e] border border-[#2d2d44] rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Database className="w-5 h-5 text-[#6D4AFF]" />
                    <span className="font-bold text-white">Service Data</span>
                  </div>
                  <ul className="text-sm space-y-2 text-gray-300 pl-8">
                    <li>• Email metadata (sender, recipient, timestamp—NOT content)</li>
                    <li>• Subscription status and plan type</li>
                    <li>• Storage usage statistics</li>
                  </ul>
                </div>

                <div className="bg-[#1a1a2e] border border-[#2d2d44] rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Globe className="w-5 h-5 text-[#6D4AFF]" />
                    <span className="font-bold text-white">Technical Data</span>
                  </div>
                  <ul className="text-sm space-y-2 text-gray-300 pl-8">
                    <li>• IP address (for security and abuse prevention only)</li>
                    <li>• Browser type and version</li>
                    <li>• Login timestamps (for security monitoring)</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3 - What We Do NOT Collect */}
          <section id="section-3" className="scroll-mt-24">
            <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-2 border-green-500/50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <span className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
                  <EyeOff className="w-5 h-5 text-white" />
                </span>
                What We Do NOT Collect
              </h2>
              
              <div className="space-y-4 text-gray-300">
                <p className="text-lg">
                  Unlike big tech email providers, we have <strong className="text-green-400">zero interest</strong> in your personal life:
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-[#0a0a18]/50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Ban className="w-4 h-4 text-green-500" />
                      <span className="font-semibold text-white">Email Content</span>
                    </div>
                    <p className="text-sm text-gray-400">Your emails are encrypted. We cannot and do not read them.</p>
                  </div>
                  
                  <div className="bg-[#0a0a18]/50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Ban className="w-4 h-4 text-green-500" />
                      <span className="font-semibold text-white">Browsing History</span>
                    </div>
                    <p className="text-sm text-gray-400">We don't track what you do outside our platform.</p>
                  </div>
                  
                  <div className="bg-[#0a0a18]/50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Ban className="w-4 h-4 text-green-500" />
                      <span className="font-semibold text-white">Location Data</span>
                    </div>
                    <p className="text-sm text-gray-400">We don't track your physical location or movements.</p>
                  </div>
                  
                  <div className="bg-[#0a0a18]/50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Ban className="w-4 h-4 text-green-500" />
                      <span className="font-semibold text-white">Purchase History</span>
                    </div>
                    <p className="text-sm text-gray-400">What you buy is your business, not ours.</p>
                  </div>
                  
                  <div className="bg-[#0a0a18]/50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Ban className="w-4 h-4 text-green-500" />
                      <span className="font-semibold text-white">Social Connections</span>
                    </div>
                    <p className="text-sm text-gray-400">We don't map your social graph or relationships.</p>
                  </div>
                  
                  <div className="bg-[#0a0a18]/50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Ban className="w-4 h-4 text-green-500" />
                      <span className="font-semibold text-white">Personal Interests</span>
                    </div>
                    <p className="text-sm text-gray-400">We don't profile you based on your communications.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4 - How We Use Your Information */}
          <section id="section-4" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#6D4AFF] rounded-lg flex items-center justify-center text-sm">4</span>
              How We Use Your Information
            </h2>
            <div className="text-gray-300 space-y-4 pl-11">
              <p>The limited information we collect is used solely for:</p>
              
              <div className="bg-[#1a1a2e] border border-[#2d2d44] rounded-xl p-6 space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#6D4AFF] mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-bold text-white">Providing the Service</span>
                    <p className="text-sm text-gray-400">Delivering your emails, managing your account, and maintaining your inbox</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#6D4AFF] mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-bold text-white">Security & Abuse Prevention</span>
                    <p className="text-sm text-gray-400">Protecting against unauthorized access, spam, and malicious activity</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#6D4AFF] mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-bold text-white">Billing & Subscriptions</span>
                    <p className="text-sm text-gray-400">Processing payments and managing your subscription (via Stripe)</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#6D4AFF] mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-bold text-white">Service Communications</span>
                    <p className="text-sm text-gray-400">Important notices about your account, security alerts, and service updates</p>
                  </div>
                </div>
              </div>
              
              <p className="text-amber-400 font-semibold">
                We will NEVER use your information for advertising, marketing, or profiling purposes.
              </p>
            </div>
          </section>

          {/* Section 5 - Data Protection & Encryption */}
          <section id="section-5" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#6D4AFF] rounded-lg flex items-center justify-center text-sm">5</span>
              Data Protection & Encryption
            </h2>
            <div className="text-gray-300 space-y-4 pl-11">
              <p>Your data is protected by multiple layers of security:</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-[#1a1a2e] to-[#252540] border border-[#2d2d44] rounded-xl p-6">
                  <Lock className="w-8 h-8 text-[#6D4AFF] mb-3" />
                  <h3 className="font-bold text-white mb-2">AES-256 Encryption</h3>
                  <p className="text-sm text-gray-400">
                    Military-grade encryption for all email content. Your messages are encrypted before they even leave your device.
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-[#1a1a2e] to-[#252540] border border-[#2d2d44] rounded-xl p-6">
                  <Server className="w-8 h-8 text-[#6D4AFF] mb-3" />
                  <h3 className="font-bold text-white mb-2">Secure Infrastructure</h3>
                  <p className="text-sm text-gray-400">
                    All data stored on secure, US-based servers with enterprise-grade physical and digital security.
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-[#1a1a2e] to-[#252540] border border-[#2d2d44] rounded-xl p-6">
                  <EyeOff className="w-8 h-8 text-[#6D4AFF] mb-3" />
                  <h3 className="font-bold text-white mb-2">Zero-Knowledge Architecture</h3>
                  <p className="text-sm text-gray-400">
                    We cannot read your emails. Only you have the keys to decrypt your messages.
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-[#1a1a2e] to-[#252540] border border-[#2d2d44] rounded-xl p-6">
                  <Shield className="w-8 h-8 text-[#6D4AFF] mb-3" />
                  <h3 className="font-bold text-white mb-2">Secure Transmission</h3>
                  <p className="text-sm text-gray-400">
                    All data transmitted over TLS/SSL encrypted connections. No unencrypted data ever leaves our servers.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 6 - No Data Sales - HIGHLIGHTED */}
          <section id="section-6" className="scroll-mt-24">
            <div className="bg-gradient-to-r from-red-500/10 via-[#6D4AFF]/20 to-blue-500/10 border-2 border-[#6D4AFF] rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <span className="w-10 h-10 bg-gradient-to-r from-red-500 to-[#6D4AFF] rounded-xl flex items-center justify-center">
                  <Ban className="w-5 h-5 text-white" />
                </span>
                No Data Sales - EVER
              </h2>
              
              <div className="space-y-6 text-gray-300">
                <div className="bg-[#0a0a18]/70 rounded-xl p-6 border border-[#6D4AFF]/30">
                  <p className="text-xl font-bold text-white mb-4">
                    We make this absolutely clear:
                  </p>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <span className="text-2xl">🚫</span>
                      <span className="text-lg">We do <strong className="text-red-400">NOT</strong> sell your personal information to data brokers</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-2xl">🚫</span>
                      <span className="text-lg">We do <strong className="text-red-400">NOT</strong> sell your information to advertisers</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-2xl">🚫</span>
                      <span className="text-lg">We do <strong className="text-red-400">NOT</strong> sell your information to marketing companies</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-2xl">🚫</span>
                      <span className="text-lg">We do <strong className="text-red-400">NOT</strong> sell your information to anyone, period</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-2xl">🚫</span>
                      <span className="text-lg">We do <strong className="text-red-400">NOT</strong> share your information for profit</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-green-400 mb-3">Our Business Model is Simple:</h3>
                  <p className="text-gray-300">
                    <strong className="text-white">You pay for the service. That's it.</strong> We make money from subscriptions, 
                    not from harvesting and selling your data. Your subscription fee is what keeps the lights on—not 
                    selling your personal information to the highest bidder.
                  </p>
                </div>

                <p className="text-sm text-gray-400 italic text-center">
                  "If you're not paying for the product, you ARE the product." At MyPatriotMail, you're the customer—never the product.
                </p>
              </div>
            </div>
          </section>

          {/* Section 7 - Third-Party Services */}
          <section id="section-7" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#6D4AFF] rounded-lg flex items-center justify-center text-sm">7</span>
              Third-Party Services
            </h2>
            <div className="text-gray-300 space-y-4 pl-11">
              <p>We use a minimal number of trusted third-party services to operate:</p>
              
              <div className="bg-[#1a1a2e] border border-[#2d2d44] rounded-xl p-6 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-[#6D4AFF]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-sm">💳</span>
                  </div>
                  <div>
                    <span className="font-bold text-white">Stripe (Payment Processing)</span>
                    <p className="text-sm text-gray-400">
                      We use Stripe to securely process payments. We never see or store your full credit card number. 
                      Stripe's privacy policy applies to payment data.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-[#6D4AFF]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-sm">☁️</span>
                  </div>
                  <div>
                    <span className="font-bold text-white">iDrive E2 (File Storage)</span>
                    <p className="text-sm text-gray-400">
                      Email attachments may be stored on encrypted cloud storage. All files are encrypted before upload.
                    </p>
                  </div>
                </div>
              </div>
              
              <p className="text-amber-400">
                <strong>Important:</strong> We have contractual agreements with all third-party providers that prohibit 
                them from using your data for any purpose other than providing their specific service.
              </p>
            </div>
          </section>

          {/* Section 8 - Data Retention */}
          <section id="section-8" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#6D4AFF] rounded-lg flex items-center justify-center text-sm">8</span>
              Data Retention
            </h2>
            <div className="text-gray-300 space-y-4 pl-11">
              <p>We keep your data only as long as necessary:</p>
              
              <div className="bg-[#1a1a2e] border border-[#2d2d44] rounded-xl p-6">
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <Trash2 className="w-5 h-5 text-[#6D4AFF] mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-bold text-white">Active Accounts</span>
                      <p className="text-sm text-gray-400">Your data is retained while your account is active and in good standing</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Trash2 className="w-5 h-5 text-[#6D4AFF] mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-bold text-white">Account Deletion</span>
                      <p className="text-sm text-gray-400">Upon account deletion request, all personal data is permanently removed within 30 days</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Trash2 className="w-5 h-5 text-[#6D4AFF] mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-bold text-white">Inactive Accounts</span>
                      <p className="text-sm text-gray-400">Accounts inactive for 12+ months may be scheduled for deletion after notification</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Trash2 className="w-5 h-5 text-[#6D4AFF] mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-bold text-white">Backup Retention</span>
                      <p className="text-sm text-gray-400">Encrypted backups are retained for up to 90 days for disaster recovery purposes</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 9 - Your Rights */}
          <section id="section-9" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#6D4AFF] rounded-lg flex items-center justify-center text-sm">9</span>
              Your Rights
            </h2>
            <div className="text-gray-300 space-y-4 pl-11">
              <p>You have full control over your personal data:</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-[#1a1a2e] border border-[#2d2d44] rounded-xl p-4">
                  <h4 className="font-bold text-white mb-2">Right to Access</h4>
                  <p className="text-sm text-gray-400">Request a copy of all personal data we hold about you</p>
                </div>
                <div className="bg-[#1a1a2e] border border-[#2d2d44] rounded-xl p-4">
                  <h4 className="font-bold text-white mb-2">Right to Correction</h4>
                  <p className="text-sm text-gray-400">Update or correct any inaccurate personal information</p>
                </div>
                <div className="bg-[#1a1a2e] border border-[#2d2d44] rounded-xl p-4">
                  <h4 className="font-bold text-white mb-2">Right to Deletion</h4>
                  <p className="text-sm text-gray-400">Request complete deletion of your account and all data</p>
                </div>
                <div className="bg-[#1a1a2e] border border-[#2d2d44] rounded-xl p-4">
                  <h4 className="font-bold text-white mb-2">Right to Export</h4>
                  <p className="text-sm text-gray-400">Download your emails and data in a portable format</p>
                </div>
              </div>
              
              <p>
                To exercise any of these rights, contact us at <strong className="text-white">privacy@mypatriotmail.org</strong>
              </p>
            </div>
          </section>

          {/* Section 10 - Contact Us */}
          <section id="section-10" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#6D4AFF] rounded-lg flex items-center justify-center text-sm">10</span>
              Contact Us
            </h2>
            <div className="text-gray-300 space-y-4 pl-11">
              <p>
                If you have questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-[#1a1a2e] border border-[#2d2d44] rounded-xl p-6 text-center">
                <Mail className="w-10 h-10 text-[#6D4AFF] mx-auto mb-4" />
                <p className="text-gray-400 mb-4">Use our secure contact form for privacy inquiries, general support, or data requests.</p>
                <Button
                  onClick={() => setContactModalOpen(true)}
                  className="bg-[#6D4AFF] hover:bg-[#5B3FD9]"
                  data-testid="privacy-contact-btn"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Contact Us
                </Button>
              </div>
              <p className="text-sm text-gray-400">
                We aim to respond to all privacy-related inquiries within 48 hours.
              </p>
            </div>
          </section>
        </div>

        {/* Final Promise Section */}
        <div className="mt-16 p-8 bg-gradient-to-r from-[#1a1a2e] to-[#252540] rounded-2xl border border-[#6D4AFF]/50 text-center">
          <Shield className="w-12 h-12 text-[#6D4AFF] mx-auto mb-4" />
          <h3 className="text-2xl font-bold mb-3">Our Promise to You</h3>
          <p className="text-gray-400 max-w-2xl mx-auto mb-6 text-lg">
            Your privacy is not for sale. Not today. Not tomorrow. <strong className="text-white">Not ever.</strong>
          </p>
          <p className="text-gray-500 text-sm">
            This policy may be updated from time to time. We will notify you of any material changes via email or prominent notice on our website.
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#2d2d44]/50 py-8 px-4 mt-16">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-500">
          <p>© 2026 MyPatriotMail. All rights reserved.</p>
          <div className="flex gap-6">
            <button onClick={() => navigate('/privacy')} className="text-[#6D4AFF]">Privacy Policy</button>
            <button onClick={() => navigate('/terms')} className="hover:text-[#6D4AFF] transition-colors">Terms of Service</button>
            <button onClick={() => navigate('/roadmap')} className="hover:text-[#6D4AFF] transition-colors">Roadmap</button>
            <button onClick={() => navigate('/')} className="hover:text-[#6D4AFF] transition-colors">Home</button>
          </div>
        </div>
      </footer>
      
      {/* Contact Form Modal */}
      <ContactFormModal
        open={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
      />
    </div>
  );
}
